"use client";

import Link from "next/link";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/24/outline";

interface CategoryPaginationProps {
  currentPage: number;
  lastPage: number;
  slug: string;
  searchParams?: { [key: string]: string | string[] | undefined };
  pathPrefix?: string;
  pathSuffix?: string;
}

export default function CategoryPagination({
  currentPage,
  lastPage,
  slug,
  searchParams = {},
  pathPrefix = "/categoria",
  pathSuffix = "",
}: CategoryPaginationProps) {
  // Don't show pagination if there's only one page
  if (lastPage <= 1) {
    return null;
  }

  // Build URL with query params
  const buildPageHref = (page: number) => {
    const safePage = Math.max(1, page);
    const normalizedPrefix = pathPrefix.endsWith("/")
      ? pathPrefix.slice(0, -1)
      : pathPrefix;
    const normalizedSuffix = pathSuffix
      ? pathSuffix.startsWith("/")
        ? pathSuffix
        : `/${pathSuffix}`
      : "";
    const basePath = `${normalizedPrefix}/${slug}${normalizedSuffix}`;

    // If page is 1 and no other params, return clean URL
    if (safePage === 1 && Object.keys(searchParams).length === 0) {
      return basePath;
    }

    const params = new URLSearchParams();

    // Add page param if not page 1
    if (safePage !== 1) {
      params.set("page", safePage.toString());
    }

    // Preserve other search params
    Object.entries(searchParams).forEach(([key, value]) => {
      if (key !== "page" && value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach((v) => params.append(key, v));
        } else {
          params.set(key, value);
        }
      }
    });

    const queryString = params.toString();
    return queryString ? `${basePath}?${queryString}` : basePath;
  };

  // Generate page numbers with ellipsis
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const showEllipsis = lastPage > 7;

    if (!showEllipsis) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= lastPage; i++) {
        pages.push(i);
      }
    } else {
      // Show first page
      pages.push(1);

      if (currentPage <= 3) {
        // Near start: show 1,2,3,4,...,last
        for (let i = 2; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(lastPage);
      } else if (currentPage >= lastPage - 2) {
        // Near end: show 1,...,last-3,last-2,last-1,last
        pages.push("...");
        for (let i = lastPage - 3; i <= lastPage; i++) {
          pages.push(i);
        }
      } else {
        // Middle: show 1,...,current-1,current,current+1,...,last
        pages.push("...");
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push("...");
        pages.push(lastPage);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();
  const prevDisabled = currentPage <= 1;
  const nextDisabled = currentPage >= lastPage;

  return (
    <nav
      className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t border-gray-200 sm:px-6"
      aria-label="Paginação"
    >
      <div className="flex items-center gap-2">
        {/* First Page */}
        {currentPage > 2 && lastPage > 3 && (
          <Link
            href={buildPageHref(1)}
            className="flex items-center justify-center w-10 h-10 rounded-xl bg-white border border-gray-200 text-gray-600 hover:bg-mygreen hover:text-white hover:border-mygreen transition-all shadow-sm"
            aria-label="Primeira página"
          >
            <ChevronDoubleLeftIcon className="w-4 h-4" />
          </Link>
        )}

        {/* Previous Page */}
        {prevDisabled ? (
          <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-100 text-gray-400 cursor-not-allowed">
            <ChevronLeftIcon className="w-5 h-5" />
          </span>
        ) : (
          <Link
            href={buildPageHref(currentPage - 1)}
            className="flex items-center justify-center w-10 h-10 rounded-xl bg-white border border-gray-200 text-gray-600 hover:bg-mygreen hover:text-white hover:border-mygreen transition-all shadow-sm"
            aria-label="Página anterior"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </Link>
        )}

        {/* Page Numbers */}
        <div className="flex items-center gap-1">
          {pageNumbers.map((page, index) =>
            page === "..." ? (
              <span
                key={`ellipsis-${index}`}
                className="flex items-center justify-center w-10 h-10 text-gray-400"
              >
                ···
              </span>
            ) : (
              <Link
                key={`page-${page}`}
                href={buildPageHref(page as number)}
                className={`flex items-center justify-center w-10 h-10 rounded-xl font-semibold transition-all ${
                  page === currentPage
                    ? "bg-mygreen text-white shadow-lg shadow-mygreen/30"
                    : "bg-white border border-gray-200 text-gray-700 hover:bg-mygreen hover:text-white hover:border-mygreen shadow-sm"
                }`}
                aria-current={page === currentPage ? "page" : undefined}
              >
                {page}
              </Link>
            )
          )}
        </div>

        {/* Next Page */}
        {nextDisabled ? (
          <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-100 text-gray-400 cursor-not-allowed">
            <ChevronRightIcon className="w-5 h-5" />
          </span>
        ) : (
          <Link
            href={buildPageHref(currentPage + 1)}
            className="flex items-center justify-center w-10 h-10 rounded-xl bg-white border border-gray-200 text-gray-600 hover:bg-mygreen hover:text-white hover:border-mygreen transition-all shadow-sm"
            aria-label="Próxima página"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </Link>
        )}

        {/* Last Page */}
        {currentPage < lastPage - 1 && lastPage > 3 && (
          <Link
            href={buildPageHref(lastPage)}
            className="flex items-center justify-center w-10 h-10 rounded-xl bg-white border border-gray-200 text-gray-600 hover:bg-mygreen hover:text-white hover:border-mygreen transition-all shadow-sm"
            aria-label="Última página"
          >
            <ChevronDoubleRightIcon className="w-4 h-4" />
          </Link>
        )}
      </div>

      {/* Page Info */}
      <p className="text-sm text-gray-500">
        Página{" "}
        <span className="font-semibold text-gray-700">{currentPage}</span> de{" "}
        <span className="font-semibold text-gray-700">{lastPage}</span>
      </p>
    </nav>
  );
}
